import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Printer } from 'lucide-react';
import { connectionLabel, receiptPrinter } from '@shripos/shared/escpos';
import { Button, Dialog, cn, useToast } from '@shripos/ui';
import { reconnectDevicePrinter } from '../../lib/printing/devices.js';
import { usePrinters, usePrinting } from '../../lib/printing/usePrinting.js';
import { kitchenOf, useSession } from '../../stores/session.js';
import { PrinterStatusBadge } from '../../components/PrinterStatus.jsx';

const DOT = {
  online: 'bg-emerald-500',
  warning: 'bg-amber-500',
  error: 'bg-red-500',
  offline: 'bg-red-500',
  unknown: 'bg-slate-400',
};

/** Billing header: receipt-printer health at a glance; tap for test print / drawer / reconnect. */
export function PrinterChip() {
  const { t } = useTranslation();
  const printers = usePrinters();
  const printing = usePrinting();
  const toast = useToast();
  const qc = useQueryClient();
  const deviceId = useSession((s) => s.device?.device?.id ?? null);
  const kitchenOrders = useSession((s) => kitchenOf(s.me).orders);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const list = (printers.data ?? []).filter((p) => p.isActive);
  const main = receiptPrinter(list);
  const state = main?.status?.state ?? 'unknown';
  const act = (key, fn) => async () => {
    setBusy(key);
    await fn();
    setBusy(null);
    qc.invalidateQueries({ queryKey: ['printers'] });
  };
  return (
    <>
      <Button
        variant="secondary"
        onClick={() => setOpen(true)}
        aria-label={
          main
            ? t('pos.printerState', 'Printer {{name}}: {{state}}', { name: main.name, state })
            : t('pos.noReceiptPrinter', 'No receipt printer')
        }
        title={
          main
            ? `${main.name}: ${main.status?.message ?? state}`
            : t('pos.browserPrintTitle', 'Receipts use the browser print dialog')
        }
        data-testid="printer-chip"
      >
        <span className="relative">
          <Printer className="size-4" />
          <span
            className={cn(
              'absolute -top-1 -right-1 size-2 rounded-full ring-2 ring-white',
              main ? DOT[state] : 'bg-slate-300',
            )}
          />
        </span>
        <span className="hidden max-w-32 truncate lg:inline">
          {main?.name ?? t('pos.browserPrinting', 'Browser printing')}
        </span>
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} title={t('pos.printers', 'Printers')} size="md">
        {!list.length ? (
          <p className="text-sm text-slate-600">
            {kitchenOrders
              ? t(
                  'pos.noPrinters',
                  "No thermal printers at this outlet: receipts and KOTs open the browser's print dialog. A manager can add printers under Printers.",
                )
              : t(
                  'pos.noPrintersNoKitchen',
                  "No thermal printers at this outlet: receipts open the browser's print dialog. A manager can add printers under Printers.",
                )}
          </p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {list.map((p) => (
              <li key={p.id} className="flex flex-wrap items-center gap-2 py-3">
                <div className="min-w-0 flex-1">
                  <p className="font-medium">{p.name}</p>
                  <p className="truncate text-xs text-slate-500">{connectionLabel(p)}</p>
                </div>
                <PrinterStatusBadge status={p.status} />
                <Button
                  size="sm"
                  variant="secondary"
                  loading={busy === `t${p.id}`}
                  onClick={act(`t${p.id}`, () => printing.test(p))}
                >
                  {t('pos.test', 'Test')}
                </Button>
                {p.profile.drawer?.enabled && (
                  <Button
                    size="sm"
                    variant="secondary"
                    loading={busy === `d${p.id}`}
                    onClick={act(`d${p.id}`, () => printing.drawer(p))}
                  >
                    {t('pos.openDrawer', 'Open drawer')}
                  </Button>
                )}
                {p.connection.via === 'device' && p.connection.deviceId === deviceId && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      reconnectDevicePrinter(p)
                        .then(() => toast.success(t('pos.printerConnected', '{{name}} connected', { name: p.name })))
                        .catch((err) => err?.name !== 'NotFoundError' && toast.error(err))
                    }
                  >
                    {t('pos.reconnect', 'Reconnect')}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Dialog>
    </>
  );
}
